import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  ROTULO_STATUS,
  ROTULO_TIPO,
  useCancelarSolicitacao,
  useContagensSolicitacoes,
  useProjetosSemAlocacao,
  useSolicitacoes,
  type Aba,
  type Solicitacao,
} from "@/api/solicitacoes";
import { AprovacaoBloqueios } from "@/components/AprovacaoBloqueios";
import { Etiqueta } from "@/components/solicitacoes/comuns";
import { data } from "@/components/solicitacoes/formato";
import { FilaDecisao } from "@/components/solicitacoes/FilaDecisao";
import { GateAceite } from "@/components/solicitacoes/GateAceite";
import { TopBar } from "@/components/TopBar";
import { useBlocks } from "@/lib/blocks-store";
import { can, useRole } from "@/lib/use-role";
import { cn } from "@/lib/utils";
import { usuarioAtual } from "@/api/registry";

export const Route = createFileRoute("/solicitacoes")({
  component: Solicitacoes,
});

type AbaTela = Aba | "bloqueios";

function Solicitacoes() {
  const role = useRole();
  const podeAprovar = can(role, "aprovarSolicitacoes");
  const ehProfissional = Boolean(usuarioAtual()?.profissionalId);

  const contagens = useContagensSolicitacoes();
  const bloqueios = useBlocks();
  const pendentesBloqueio = bloqueios.filter((b) => b.status === "PENDENTE").length;

  // A escolhida só depois que o usuário clica. Antes disso, a primeira aba
  // visível — o perfil só é conhecido depois que o registry hidrata, então um
  // valor inicial congelado deixaria o Planejamento numa aba que nem existe.
  const [escolhida, setEscolhida] = useState<AbaTela | null>(null);

  const abas: { valor: AbaTela; rotulo: string; badge?: number; visivel: boolean }[] = [
    {
      valor: "fila",
      rotulo: "Fila do Planejamento",
      badge: contagens.data?.fila,
      visivel: podeAprovar,
    },
    {
      valor: "aceites",
      rotulo: "Aguardando meu aceite",
      badge: contagens.data?.aceites,
      visivel: ehProfissional,
    },
    { valor: "minhas", rotulo: "Meus pedidos", visivel: true },
    {
      valor: "bloqueios",
      rotulo: "Bloqueios de agenda",
      badge: pendentesBloqueio,
      visivel: podeAprovar,
    },
  ];

  const visiveis = abas.filter((a) => a.visivel);
  const aba = visiveis.some((a) => a.valor === escolhida)
    ? escolhida!
    : (visiveis[0]?.valor ?? "minhas");
  const setAba = setEscolhida;

  return (
    <>
      <TopBar
        title="Solicitações"
        subtitle="Nenhuma alocação entra na grade sem passar por aqui e pelo aceite do profissional"
      />

      <div className="flex-1 overflow-auto">
        <div className="border-b border-border bg-card px-4 py-2">
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            {visiveis.map((a) => (
              <button
                key={a.valor}
                onClick={() => setAba(a.valor)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium",
                  aba === a.valor
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {a.rotulo}
                {!!a.badge && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 text-[10px] font-semibold",
                      aba === a.valor
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-health-yellow/20 text-health-yellow",
                    )}
                  >
                    {a.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {aba === "bloqueios" ? (
          <div className="p-4">
            <AprovacaoBloqueios />
          </div>
        ) : (
          <>
            {aba === "fila" && <SemAlocacao />}
            <div className="p-4">
              <Lista aba={aba} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Lista({ aba }: { aba: Aba }) {
  const consulta = useSolicitacoes(aba);

  if (consulta.isPending) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando…
      </div>
    );
  }

  const itens = consulta.data ?? [];
  if (aba === "fila") return <FilaDecisao itens={itens} />;
  if (aba === "aceites") return <GateAceite itens={itens} />;
  return <MeusPedidos itens={itens} />;
}

function MeusPedidos({ itens }: { itens: Solicitacao[] }) {
  const cancelar = useCancelarSolicitacao();

  if (!itens.length) {
    return (
      <p className="rounded-lg border border-dashed border-border p-6 text-center text-[11px] text-muted-foreground">
        Você ainda não abriu nenhuma solicitação. Elas nascem no slot da grade Flow.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {itens.map((s) => (
        <div key={s.id} className="rounded-lg border border-border bg-card p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Etiqueta>{ROTULO_TIPO[s.tipo]}</Etiqueta>
            <span className="text-xs font-semibold text-foreground">{s.projeto.os}</span>
            <Etiqueta tom={tomDoStatus(s.status)}>{ROTULO_STATUS[s.status]}</Etiqueta>
            <span className="ml-auto text-[10px] text-muted-foreground">{data(s.criadoEm)}</span>
          </div>

          <p className="mt-1.5 text-[11px] text-foreground">
            {s.profissional.nome} em {data(s.inicio)}
            {s.quantidade != null && ` · ${s.quantidade} ${s.unidade === "HORAS" ? "h" : "slides"}`}
          </p>

          {s.motivoDecisao && (
            <p className="mt-1 text-[11px] text-health-red">Motivo: {s.motivoDecisao}</p>
          )}
          {s.ajusteAceite && (
            <p className="mt-1 text-[11px] text-health-yellow">
              Ajuste pedido pelo profissional: {s.ajusteAceite}
            </p>
          )}

          {s.status === "PENDENTE" && (
            <div className="mt-2 flex justify-end border-t border-border/60 pt-2">
              <button
                onClick={() =>
                  cancelar.mutate(s.id, {
                    onSuccess: () => toast.success("Pedido cancelado"),
                    onError: (e) =>
                      toast.error(e instanceof Error ? e.message : "Não foi possível cancelar"),
                  })
                }
                className="text-[10px] text-muted-foreground hover:text-health-red"
              >
                Cancelar pedido
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** RF-52 — o que ainda não tem alocação formalizada é o que falta pedir. */
function SemAlocacao() {
  const projetos = useProjetosSemAlocacao();
  const itens = projetos.data ?? [];

  return (
    <section className="border-b border-border bg-card px-4 py-3">
      <div className="mb-2 flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 text-health-yellow" />
        <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground">
          Projetos sem alocação formalizada
        </h2>
        <span className="rounded-full bg-health-yellow/15 px-2 py-0.5 text-[10px] font-semibold text-health-yellow">
          {itens.length}
        </span>
      </div>

      {itens.length ? (
        <div className="flex flex-wrap gap-2">
          {itens.map((p) => (
            <Link
              key={p.id}
              to="/card/$id"
              params={{ id: p.id }}
              className={cn(
                "flex min-w-[240px] flex-1 items-center gap-2 rounded-lg border px-3 py-2 hover:bg-secondary/40",
                p.emAprovacao ? "border-primary/40" : "border-dashed border-health-yellow/60",
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-foreground">{p.os}</p>
                <p className="text-[11px] text-muted-foreground">
                  {p.emAprovacao
                    ? `${p.emAprovacao} solicitação(ões) em andamento`
                    : "Nenhum slot na grade"}
                  {p.vFinal && ` · V Final em ${data(p.vFinal)}`}
                </p>
              </div>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          Todos os projetos ativos já têm alocação formalizada na grade.
        </p>
      )}
    </section>
  );
}

const tomDoStatus = (status: Solicitacao["status"]) =>
  status === "APROVADA"
    ? "verde"
    : status === "RECUSADA" || status === "CANCELADA"
      ? "vermelho"
      : status === "DEVOLVIDA"
        ? "amarelo"
        : "primario";
